import * as THREE from 'three';
import {
  CONTAINER,
  DANGER_HOLD_TIME,
  DANGER_LINE_Y,
  DROP,
  PHYSICS,
  TIER_COUNT,
} from '../config';
import { getTier, TIERS } from '../art/tiers';
import { Confection } from '../entities/Confection';
import { PhysicsWorld } from './PhysicsWorld';
import { ScoreSystem } from './ScoreSystem';
import { VFXSystem } from './VFX';
import { SceneRig } from '../presentation/SceneRig';
import { InputManager } from '../input/InputManager';
import { AudioEngine } from '../audio/AudioEngine';
import { UIManager, type Screen, type SettingsState } from '../ui/UIManager';
import { clamp, pickWeighted } from '../utils/math';

type Phase = 'menu' | 'playing' | 'paused' | 'gameover';

// prevScreen used for settings/scores navigation stack

interface PendingMerge {
  a: Confection;
  b: Confection;
  chainIndex: number;
}

export class Game {
  private rig: SceneRig;
  private physics = new PhysicsWorld();
  private audio = new AudioEngine();
  private input: InputManager;
  private ui: UIManager;
  private vfx!: VFXSystem;
  private score = new ScoreSystem();

  private pieces: Confection[] = [];
  private bodyToPiece = new Map<number, Confection>();
  private current: Confection | null = null;
  private nextTier = 0;
  private dropCooldown = 0;
  private canDrop = true;
  private spawnDelay = 0;
  private phase: Phase = 'menu';
  private prevScreen: Screen = 'menu';

  private guideLine: THREE.Line | null = null;

  private time = 0;
  private dropCount = 0;
  private maxChainThisGame = 0;
  private running = false;
  private lastTs = 0;
  private mergeQueue: PendingMerge[] = [];
  private mergingPairs = new Set<string>();

  constructor(canvas: HTMLCanvasElement, uiRoot: HTMLElement) {
    this.rig = new SceneRig(canvas);
    this.input = new InputManager(canvas);
    this.input.setBounds(CONTAINER.width / 2 - 0.2);
    this.input.setEnabled(false);

    this.ui = new UIManager(uiRoot, {
      onStart: () => void this.startGame(),
      onResume: () => this.resume(),
      onRestart: () => void this.startGame(),
      onPause: () => this.pause(),
      onOpenSettings: () => this.openSettings(),
      onOpenScores: () => {
        this.prevScreen = this.ui.getScreen();
        this.ui.show('scores');
      },
      onOpenTutorial: () => this.ui.show('tutorial'),
      onBackToMenu: () => this.handleBack(),
      onSettingsChange: (s) => this.applySettings(s),
    });

    this.vfx = new VFXSystem(this.rig.scene, uiRoot);
    this.buildGuide();
    this.applySettings(this.ui.settings);
    this.resize();
    window.addEventListener('resize', () => this.resize());
    window.addEventListener('visibilitychange', () => {
      if (document.hidden && this.phase === 'playing') this.pause();
    });
  }

  async init(): Promise<void> {
    await this.physics.init();
    this.running = true;
    this.lastTs = performance.now();
    requestAnimationFrame((t) => this.loop(t));
  }

  private applySettings(s: SettingsState): void {
    this.audio.setMusic(s.music);
    this.audio.setSfx(s.sfx);
    this.audio.setVolume(s.volume);
    this.rig.setQuality(s.quality);
    this.vfx.setQuality(s.quality);
  }

  private buildGuide(): void {
    const geo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, -1, 0),
    ]);
    const mat = new THREE.LineDashedMaterial({
      color: 0xffe0ff,
      dashSize: 0.2,
      gapSize: 0.12,
      transparent: true,
      opacity: 0.4,
    });
    this.guideLine = new THREE.Line(geo, mat);
    this.guideLine.computeLineDistances();
    this.guideLine.visible = false;
    this.rig.scene.add(this.guideLine);
  }

  private resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.rig.resize(w, h);
  }

  private async startGame(): Promise<void> {
    await this.audio.unlock();
    this.audio.playUIConfirm();
    this.clearBoard();
    this.score.reset();
    this.dropCount = 0;
    this.maxChainThisGame = 0;
    this.dropCooldown = 0;
    this.canDrop = true;
    this.nextTier = this.rollTier();
    this.phase = 'playing';
    this.input.setEnabled(true);
    this.ui.show('playing');
    this.ui.updateBestDisplay();
    this.spawnCurrent();
  }

  private clearBoard(): void {
    for (const p of this.pieces) {
      p.dispose(this.physics.world);
    }
    this.pieces = [];
    this.bodyToPiece.clear();
    if (this.current) {
      this.current.dispose(this.physics.world);
      this.current = null;
    }
    this.mergeQueue = [];
    this.mergingPairs.clear();
  }

  private rollTier(): number {
    const weights = DROP.weights;
    const idx = pickWeighted(weights);
    return clamp(idx, 0, DROP.spawnMaxTier);
  }

  private spawnCurrent(): void {
    const tier = this.nextTier;
    this.nextTier = this.rollTier();
    const c = new Confection(tier);
    const x = clamp(this.input.aimX, this.aimMin(c), this.aimMax(c));
    const y = PHYSICS.spawnHeight;
    c.createPhysics(this.physics.world, x, y, false);
    c.setKinematic(this.physics.world, true);
    this.rig.scene.add(c.mesh);
    this.current = c;
    this.updateGuide();
    this.syncNextPreview();
  }

  private aimMin(c: Confection): number {
    return -CONTAINER.width / 2 + c.radius + 0.08;
  }

  private aimMax(c: Confection): number {
    return CONTAINER.width / 2 - c.radius - 0.08;
  }

  private syncNextPreview(): void {
    /* HUD pulls nextTier */
  }

  private dropCurrent(): void {
    if (!this.current || !this.canDrop || this.dropCooldown > 0 || this.phase !== 'playing') return;
    const c = this.current;
    c.setKinematic(this.physics.world, false);
    // Small downward nudge
    c.applyImpulse(0, -0.4);
    this.registerPiece(c);
    this.current = null;
    this.canDrop = false;
    this.dropCooldown = PHYSICS.dropCooldown;
    this.spawnDelay = PHYSICS.dropCooldown * 0.85;
    this.dropCount++;
    this.audio.playDrop();
    this.audio.playWhoosh();
  }

  private registerPiece(c: Confection): void {
    this.pieces.push(c);
    if (c.body) {
      this.bodyToPiece.set(c.body.handle, c);
    }
  }

  private unregisterPiece(c: Confection): void {
    if (c.body) this.bodyToPiece.delete(c.body.handle);
    const idx = this.pieces.indexOf(c);
    if (idx >= 0) this.pieces.splice(idx, 1);
  }

  private pause(): void {
    if (this.phase !== 'playing') return;
    this.phase = 'paused';
    this.input.setEnabled(false);
    this.ui.show('paused');
    this.audio.playUIClick();
  }

  private resume(): void {
    if (this.phase !== 'paused' && this.ui.getScreen() !== 'settings') {
      // from settings during pause
    }
    if (this.phase === 'gameover' || this.phase === 'menu') return;
    this.phase = 'playing';
    this.input.setEnabled(true);
    this.ui.show('playing');
    this.audio.playUIClick();
    this.lastTs = performance.now();
  }

  private openSettings(): void {
    this.prevScreen = this.ui.getScreen();
    if (this.phase === 'playing') {
      this.phase = 'paused';
      this.input.setEnabled(false);
    }
    this.ui.show('settings');
    this.audio.playUIClick();
  }

  private handleBack(): void {
    this.audio.playUIClick();
    const screen = this.ui.getScreen();
    if (screen === 'settings' || screen === 'scores' || screen === 'tutorial') {
      if (this.phase === 'paused' || this.prevScreen === 'paused' || this.prevScreen === 'playing') {
        this.ui.show('paused');
        return;
      }
      if (this.phase === 'gameover' || this.prevScreen === 'gameover') {
        this.ui.show('gameover');
        return;
      }
      this.phase = 'menu';
      this.input.setEnabled(false);
      this.ui.show('menu');
      return;
    }
    if (screen === 'paused' || screen === 'gameover') {
      this.clearBoard();
      this.phase = 'menu';
      this.input.setEnabled(false);
      this.ui.show('menu');
      return;
    }
    this.phase = 'menu';
    this.input.setEnabled(false);
    this.ui.show('menu');
  }

  private gameOver(): void {
    if (this.phase === 'gameover') return;
    this.phase = 'gameover';
    this.input.setEnabled(false);
    this.audio.playGameOver();
    this.audio.setDanger(0);

    const prevBest = ScoreSystem.loadHighScores()[0]?.score ?? 0;
    const isNewBest = this.score.score > prevBest;
    ScoreSystem.saveHighScore({
      score: this.score.score,
      maxTier: this.score.maxTierReached,
      date: new Date().toISOString(),
    });
    if (this.score.maxTierReached >= TIER_COUNT - 1) {
      this.audio.playWinFanfare();
    }
    this.ui.showGameOver({
      score: this.score.score,
      maxTier: this.score.maxTierReached,
      merges: this.score.merges,
      chain: this.maxChainThisGame,
      isNewBest,
    });
  }

  private updateGuide(): void {
    if (!this.guideLine || !this.current || this.phase !== 'playing') {
      if (this.guideLine) this.guideLine.visible = false;
      return;
    }
    const c = this.current;
    const x = clamp(this.input.aimX, this.aimMin(c), this.aimMax(c));
    const y = PHYSICS.spawnHeight;
    c.setTranslation(x, y);

    // Ready pulse on held piece
    const ready = this.canDrop && this.dropCooldown <= 0;
    const mat = c.mesh.children[0] as THREE.Mesh | undefined;
    if (mat && mat.material && !Array.isArray(mat.material)) {
      const m = mat.material as THREE.MeshPhysicalMaterial;
      if ('emissiveIntensity' in m) {
        m.emissiveIntensity = c.def.emissiveIntensity * (ready ? 1 + Math.sin(this.time * 6) * 0.15 : 0.55);
        m.transparent = !ready;
        m.opacity = ready ? 1 : 0.72;
      }
    }

    const halfH = CONTAINER.height / 2;
    const positions = this.guideLine.geometry.attributes.position as THREE.BufferAttribute;
    positions.setXYZ(0, x, y - c.radius * 0.5, 0.1);
    positions.setXYZ(1, x, -halfH + 0.1, 0.1);
    positions.needsUpdate = true;
    this.guideLine.computeLineDistances();
    this.guideLine.visible = true;
    const lineMat = this.guideLine.material as THREE.LineDashedMaterial;
    lineMat.opacity = ready ? 0.5 : 0.18;
    lineMat.color.setHex(ready ? 0xffe0ff : 0x8866aa);
  }

  private processCollisions(): void {
    const pairs = this.physics.drainCollisionPairs();
    for (const [h1, h2] of pairs) {
      const c1 = this.findByCollider(h1);
      const c2 = this.findByCollider(h2);
      if (!c1 || !c2) continue;
      if (c1.merging || c2.merging || c1.markedForRemoval || c2.markedForRemoval) continue;

      // Impact feedback
      const v1 = c1.body?.linvel();
      const v2 = c2.body?.linvel();
      const rel = Math.hypot((v1?.x ?? 0) - (v2?.x ?? 0), (v1?.y ?? 0) - (v2?.y ?? 0));
      if (rel > 1.5) {
        const str = clamp(rel / 10, 0.1, 1);
        c1.onImpact(str);
        c2.onImpact(str);
        this.audio.playImpact(str, (c1.def.voicePitch + c2.def.voicePitch) / 2);
      }

      this.tryQueueMerge(c1, c2, 0);
    }

    // Continuous proximity merges (settled contacts that never re-trigger collision start)
    this.scanProximityMerges();
  }

  private tryQueueMerge(a: Confection, b: Confection, chainIndex: number): void {
    if (a.merging || b.merging || a.markedForRemoval || b.markedForRemoval) return;
    if (a.tier !== b.tier || a.tier >= TIER_COUNT - 1) return;
    const key = pairKey(a.id, b.id);
    if (this.mergingPairs.has(key)) return;
    this.mergingPairs.add(key);
    this.mergeQueue.push({ a, b, chainIndex });
  }

  private scanProximityMerges(): void {
    const n = this.pieces.length;
    for (let i = 0; i < n; i++) {
      const a = this.pieces[i]!;
      if (a.merging || a.markedForRemoval) continue;
      const pa = a.getPosition();
      for (let j = i + 1; j < n; j++) {
        const b = this.pieces[j]!;
        if (b.merging || b.markedForRemoval) continue;
        if (a.tier !== b.tier || a.tier >= TIER_COUNT - 1) continue;
        const pb = b.getPosition();
        const dist = Math.hypot(pa.x - pb.x, pa.y - pb.y);
        const touch = (a.radius + b.radius) * 0.98;
        if (dist <= touch) {
          // Require both relatively settled so mid-air grazes don't force merges
          const va = a.body?.linvel();
          const vb = b.body?.linvel();
          const speed =
            Math.hypot(va?.x ?? 0, va?.y ?? 0) + Math.hypot(vb?.x ?? 0, vb?.y ?? 0);
          if (speed < 6.5) this.tryQueueMerge(a, b, 0);
        }
      }
    }
  }

  private findByCollider(handle: number): Confection | null {
    // Rapier event gives collider handles
    try {
      const coll = this.physics.world.getCollider(handle);
      if (!coll) return null;
      const body = coll.parent();
      if (!body) return null;
      return this.bodyToPiece.get(body.handle) ?? null;
    } catch {
      return null;
    }
  }

  private processMerges(): void {
    if (this.mergeQueue.length === 0) return;
    // Process all queued merges this frame (chain support)
    const queue = this.mergeQueue.splice(0);
    // Sort so we don't double-use a piece
    const used = new Set<number>();

    for (const m of queue) {
      if (used.has(m.a.id) || used.has(m.b.id)) continue;
      if (m.a.markedForRemoval || m.b.markedForRemoval) continue;
      if (m.a.tier !== m.b.tier) continue;
      if (m.a.tier >= TIER_COUNT - 1) continue;

      used.add(m.a.id);
      used.add(m.b.id);
      this.executeMerge(m.a, m.b, m.chainIndex);
    }
    this.mergingPairs.clear();
  }

  private executeMerge(a: Confection, b: Confection, chainIndex: number): void {
    a.merging = true;
    b.merging = true;
    a.markedForRemoval = true;
    b.markedForRemoval = true;

    const pa = a.getPosition();
    const pb = b.getPosition();
    // Mass-weighted center
    const ma = a.radius ** 3;
    const mb = b.radius ** 3;
    const mx = (pa.x * ma + pb.x * mb) / (ma + mb);
    const my = (pa.y * ma + pb.y * mb) / (ma + mb);

    const newTier = a.tier + 1;
    const def = getTier(newTier);

    // Remove old
    this.unregisterPiece(a);
    this.unregisterPiece(b);
    a.dispose(this.physics.world);
    b.dispose(this.physics.world);

    // Create upgraded
    const neu = new Confection(newTier);
    neu.createPhysics(this.physics.world, mx, my, true);
    neu.playMergeBirth();
    // Soft settle velocity
    if (neu.body) {
      const va = a.body?.linvel();
      const vb = b.body?.linvel();
      // bodies already removed — use zero-ish
      void va;
      void vb;
      neu.body.setLinvel({ x: 0, y: 0.15, z: 0 }, true);
      neu.body.setAngvel({ x: 0, y: 0, z: (Math.random() - 0.5) * 1.5 }, true);
    }
    this.rig.scene.add(neu.mesh);
    this.registerPiece(neu);

    // Impulse neighbors
    const impulseR = PHYSICS.mergeImpulseRadius + neu.radius;
    for (const p of this.pieces) {
      if (p.id === neu.id || p.merging) continue;
      const pp = p.getPosition();
      const dx = pp.x - mx;
      const dy = pp.y - my;
      const dist = Math.hypot(dx, dy);
      if (dist < impulseR && dist > 0.01) {
        const str = PHYSICS.mergeImpulseStrength * (1 - dist / impulseR) * (1 + newTier * 0.08);
        p.applyImpulse((dx / dist) * str, (dy / dist) * str + 0.2 * str);
        p.onImpact(str * 0.5);
      }
    }

    // Score / VFX / audio
    const gain = this.score.registerMerge(newTier, chainIndex);
    this.maxChainThisGame = Math.max(this.maxChainThisGame, this.score.combo);
    this.vfx.burst(mx, my, def.particleColor, 14 + newTier * 4, 2.5 + newTier * 0.2);
    this.vfx.ringBurst(mx, my, def.accent, neu.radius);
    this.vfx.flash(mx, my, def.emissive, neu.radius * 1.4);
    this.vfx.floatScore(mx, my, `+${gain}`, this.score.combo >= 2);
    this.audio.playMerge(newTier, this.score.combo);
    if (newTier >= 5) this.rig.shake(0.06 + newTier * 0.012);
    if (newTier >= TIER_COUNT - 1) this.audio.playWinFanfare();

    // Same-frame chain: check neighbors of new piece for same tier after a brief physics settle
    this.scheduleChainCheck(neu, chainIndex + 1);
  }

  private scheduleChainCheck(piece: Confection, chainIndex: number): void {
    // Defer one frame-ish so collider exists
    requestAnimationFrame(() => {
      if (this.phase !== 'playing' || piece.markedForRemoval) return;
      const pos = piece.getPosition();
      for (const other of this.pieces) {
        if (other.id === piece.id || other.merging || other.markedForRemoval) continue;
        if (other.tier !== piece.tier) continue;
        if (piece.tier >= TIER_COUNT - 1) continue;
        const op = other.getPosition();
        const dist = Math.hypot(op.x - pos.x, op.y - pos.y);
        const minDist = (piece.radius + other.radius) * 0.95;
        if (dist < minDist) {
          const key = pairKey(piece.id, other.id);
          if (!this.mergingPairs.has(key)) {
            this.mergingPairs.add(key);
            this.mergeQueue.push({ a: piece, b: other, chainIndex });
          }
        }
      }
    });
  }

  private updateDanger(dt: number): void {
    let maxDanger = 0;
    let anyAbove = false;
    for (const p of this.pieces) {
      if (p.isAboveDanger(DANGER_LINE_Y)) {
        // Exempt only pieces still rocketing upward from a merge impulse
        const lv = p.body?.linvel();
        const rocketingUp = !!lv && lv.y > 2.2;
        if (!rocketingUp) {
          p.dangerTime += dt;
          anyAbove = true;
          maxDanger = Math.max(maxDanger, p.dangerTime / DANGER_HOLD_TIME);
        }
      } else {
        p.dangerTime = Math.max(0, p.dangerTime - dt * 2.5);
      }
    }
    // Current held piece doesn't count (not in pieces until dropped)
    this.rig.setDangerLevel(clamp(maxDanger, 0, 1));
    this.audio.setDanger(anyAbove ? clamp(maxDanger, 0, 1) : 0);
    if (maxDanger >= 1) {
      this.gameOver();
    }
  }

  private loop(ts: number): void {
    if (!this.running) return;
    let dt = (ts - this.lastTs) / 1000;
    this.lastTs = ts;
    // Tab recovery
    if (dt > 0.1) dt = PHYSICS.timeStep;
    dt = Math.min(dt, 0.05);
    this.time += dt;

    this.input.update(dt);

    if (this.phase === 'playing') {
      if (this.input.consumePause()) this.pause();
      if (this.input.consumeDrop()) this.dropCurrent();

      if (this.dropCooldown > 0) this.dropCooldown -= dt;
      if (this.spawnDelay > 0) {
        this.spawnDelay -= dt;
        if (this.spawnDelay <= 0 && !this.current && this.phase === 'playing') {
          this.spawnCurrent();
          this.canDrop = true;
        }
      }

      this.updateGuide();
      this.physics.step(dt);
      this.processCollisions();
      this.processMerges();

      for (const p of this.pieces) {
        p.syncFromPhysics(dt, this.time);
      }
      if (this.current) {
        this.current.updateVisual(dt, this.time);
      }

      this.updateDanger(dt);
      this.score.update(dt);

      this.ui.updateHud({
        score: this.score.score,
        lastGain: this.score.lastGain,
        lastGainTimer: this.score.lastGainTimer,
        combo: this.score.combo,
        nextTier: this.nextTier,
        dangerRatio: this.getDangerRatio(),
        showHint: this.dropCount < 3,
      });
    } else {
      // Still animate visuals lightly on menu
      this.input.consumeDrop();
      this.input.consumePause();
    }

    this.rig.update(dt);
    this.vfx.update(dt);
    this.rig.render();

    requestAnimationFrame((t) => this.loop(t));
  }

  private getDangerRatio(): number {
    let max = 0;
    for (const p of this.pieces) {
      max = Math.max(max, p.dangerTime / DANGER_HOLD_TIME);
    }
    return max;
  }

  dispose(): void {
    this.running = false;
    this.clearBoard();
    this.physics.dispose();
    this.vfx.dispose();
    this.audio.dispose();
    this.rig.dispose();
  }
}

function pairKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

// silence unused in case of tree shake issues
void TIERS;
